import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';

const SearchableMedicineDropdown = ({
    value = '',
    onChange,
    placeholder = 'Select Medicine...',
    disabled = false,
    className = '',
    error = false,
    onKeyDown: parentOnKeyDown,
    defaultDisplay = '',
    autoFocus = false,
    id = ''
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [medicines, setMedicines] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMedicine, setSelectedMedicine] = useState(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    const mainDivRef = useRef(null);
    const listRef = useRef(null);

    // Auto-focus logic
    useEffect(() => {
        if (autoFocus && !disabled) {
            setIsOpen(true);
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [autoFocus, disabled]);

    // Find selected medicine details
    useEffect(() => {
        if (value && medicines.length > 0) {
            const found = medicines.find(med => med.id === parseInt(value));
            setSelectedMedicine(found);
        } else {
            if (!value) setSelectedMedicine(null);
        }
    }, [value, medicines]);

    // Debounced search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (isOpen) {
                fetchMedicines(searchTerm);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, isOpen]);

    // Reset highlighted index when medicines change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [medicines]);

    // Fetch medicines initially when dropdown opens
    useEffect(() => {
        if (isOpen && medicines.length === 0) {
            fetchMedicines('');
        }
    }, [isOpen]);

    // Fetch medicines initially if there's a value but no medicines loaded (for edit mode)
    useEffect(() => {
        if (value && medicines.length === 0 && !loading) {
            fetchMedicines('');
        }
    }, [value, medicines.length, loading]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Scroll highlighted item into view
    useEffect(() => {
        if (isOpen && highlightedIndex >= 0 && listRef.current) {
            const listNode = listRef.current;
            const imgNode = listNode.children[highlightedIndex];
            if (imgNode) {
                imgNode.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, isOpen]);

    const fetchMedicines = async (search = '') => {
        setLoading(true);
        try {
            // Use correct endpoint: /medicine/search for search functionality
            let url = '/medicine/search?limit=100&page=1';
            if (search.trim()) {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }

            const response = await axiosInstance.get(url);
            // Handle both response formats
            const medicineData = response.data.medicines || response.data.data || [];
            setMedicines(medicineData);

        } catch (error) {
            console.error('Error fetching medicines:', error);
            setMedicines([]);
        } finally {
            setLoading(false);
        }
    };

    const handleMedicineSelect = (medicine, refocus = true) => {
        setSelectedMedicine(medicine);
        onChange(medicine);
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);

        if (refocus) {
            // Return focus to main div so user can tab/enter to next field
            setTimeout(() => mainDivRef.current?.focus(), 50);
        }
    };

    const openDropdown = () => {
        if (disabled) return;
        setIsOpen(true);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    };

    const handleToggleDropdown = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    // Modified Key Handler
    const handleKeyDown = (e) => {
        // If Dropdown is OPEN, handle navigation
        if (isOpen) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < medicines.length - 1 ? prev + 1 : prev
                );
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (highlightedIndex >= 0 && medicines[highlightedIndex]) {
                    // Select medicine WITHOUT refocusing main div (we will move next manually)
                    handleMedicineSelect(medicines[highlightedIndex], false);

                    // Manually find next focusable element relative to mainDivRef
                    setTimeout(() => {
                        const focusable = document.querySelectorAll('input, select, textarea, button, [tabindex="0"]');
                        const list = Array.from(focusable).filter(el => !el.disabled && el.tabIndex !== -1);
                        const index = list.indexOf(mainDivRef.current);
                        if (index > -1 && index < list.length - 1) {
                            list[index + 1].focus();
                        }
                    }, 50);
                }
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                setIsOpen(false);
                mainDivRef.current?.focus();
                return;
            }
        } else {
            // If Dropdown is CLOSED
            if (e.key === 'Enter' || e.key === 'ArrowDown') {
                e.preventDefault();
                e.stopPropagation();
                openDropdown();
                return;
            }
        }

        if (parentOnKeyDown) {
            parentOnKeyDown(e);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setHighlightedIndex(-1); // Reset highlight on search
    };

    const handleClear = (e) => {
        e.stopPropagation();
        setSelectedMedicine(null);
        onChange(null);
        setSearchTerm('');
    };

    const displayText = selectedMedicine
        ? `${selectedMedicine.name} - ${selectedMedicine.manufacturer}`
        : defaultDisplay || placeholder;

    const borderColor = error
        ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-200'
        : 'border-gray-300 focus-within:border-teal-500 focus-within:ring-teal-200';

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Main Input */}
            <div
                ref={mainDivRef}
                tabIndex={disabled ? -1 : 0}
                className={`
                    w-full px-3 py-2 bg-white border rounded-lg cursor-pointer outline-none
                    flex items-center justify-between ring-2 ring-transparent
                    ${borderColor}
                    ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-400'}
                `}
                onClick={handleToggleDropdown}
                onKeyDown={handleKeyDown}
                onFocus={() => !isOpen && !disabled && mainDivRef.current?.classList.add('ring-teal-500')}
                onBlur={() => mainDivRef.current?.classList.remove('ring-teal-500')}
            >
                <span className={`
                    truncate flex-1
                    ${selectedMedicine ? 'text-gray-900' : 'text-gray-500'}
                    ${disabled ? 'text-gray-400' : ''}
                `}>
                    {displayText}
                </span>

                <div className="flex items-center gap-2">
                    {selectedMedicine && !disabled && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    )}
                    <ChevronDown className={`
                        w-5 h-5 text-gray-400 transition-transform
                        ${isOpen ? 'transform rotate-180' : ''}
                    `} />
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeyDown} // Pass key events here too
                                placeholder="Search medicines..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-500"
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div
                        ref={listRef}
                        className="overflow-y-auto max-h-60"
                        onKeyDown={handleKeyDown} // Handle keys on list container too
                    >
                        {loading ? (
                            <div className="p-4 text-center text-gray-500">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600 mx-auto"></div>
                                <div className="mt-2">Loading medicines...</div>
                            </div>
                        ) : medicines.length === 0 ? (
                            <div className="p-4 text-center text-gray-500">
                                {searchTerm ? 'No medicines found' : 'No medicines available'}
                            </div>
                        ) : (
                            medicines.map((medicine, index) => (
                                <div
                                    key={medicine.id}
                                    onClick={() => handleMedicineSelect(medicine)}
                                    // Use highlightedIndex for styling
                                    className={`
                                        px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0
                                        hover:bg-teal-50 transition-colors
                                        ${selectedMedicine?.id === medicine.id ? 'bg-teal-100' : ''}
                                        ${index === highlightedIndex ? 'bg-teal-50 ring-1 ring-inset ring-teal-300' : ''}
                                    `}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-gray-900 truncate">
                                                {medicine.name}
                                            </div>
                                            <div className="text-sm text-gray-500 truncate">
                                                {medicine.manufacturer} • {medicine.uom} • {medicine.pack}
                                            </div>
                                            {medicine.generic_name && medicine.generic_name !== 'NA' && (
                                                <div className="text-xs text-gray-400 truncate">
                                                    {medicine.generic_name}
                                                </div>
                                            )}
                                        </div>
                                        {selectedMedicine?.id === medicine.id && (
                                            <Check className="w-4 h-4 text-teal-600 ml-2" />
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {medicines.length > 0 && (
                        <div className="p-2 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 text-center">
                            Showing {medicines.length} medicine{medicines.length !== 1 ? 's' : ''}
                            {searchTerm && ` for "${searchTerm}"`}
                            <div className="mt-1 text-gray-400 text-[10px]">Use Arrows to navigate, Enter to select</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableMedicineDropdown;